import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { humanizeField } from './ContractReview.Functions';

/*
 * Textos de la revisión con IA en lenguaje de usuario. El servicio habla con los nombres del modelo de Contracts
 * (`PaymentScheduleFrequency`, `ContractPaymentScheduleTypeEnum`, `monthly`…); nada de eso tiene que llegar a la pantalla.
 * Los diccionarios viven en i18n (`contractReview.field` y `contractReview.enumValue`, es/en/pr).
 */

type Dictionary = Record<string, string>;

const EMPTY: Dictionary = {};

/** Un diccionario de i18n como objeto (para buscar sin distinguir mayúsculas y para reemplazar dentro de textos). */
const useDictionary = (group: 'field' | 'enumValue'): Dictionary => {
  const { i18n } = useTranslation();
  return useMemo(() => {
    const read = (lng: string): Dictionary | undefined => {
      const bundle = i18n.getResourceBundle?.(lng, 'translation') as { contractReview?: Record<string, unknown> } | undefined;
      const value = bundle?.contractReview?.[group];
      return value && typeof value === 'object' ? (value as Dictionary) : undefined;
    };
    return read(i18n.language) ?? read('es') ?? EMPTY;
  }, [i18n, i18n.language, group]);
};

const lowerIndex = (dictionary: Dictionary): Dictionary => Object.fromEntries(Object.entries(dictionary).map(([key, value]) => [key.toLowerCase(), value]));

/** Texto i18n de un código de la lista cerrada; el código crudo si no está traducido (tolerante a códigos nuevos). */
export const useCodeText = () => {
  const { t } = useTranslation();
  return (group: string, value: string | null | undefined): string => {
    if (!value) return '—';
    const key = `contractReview.${group}.${value}`;
    const text = t(key);
    return text === key || typeof text !== 'string' ? value : text;
  };
};

/**
 * Nombre de un campo del modelo en el idioma del usuario: "PaymentScheduleFrequency" o "contract.paymentScheduleFrequency"
 * → "Frecuencia de pago". Sin traducción, se separa en palabras.
 */
export const useFieldLabel = () => {
  const fields = useDictionary('field');
  const index = useMemo(() => lowerIndex(fields), [fields]);
  return useCallback(
    (field: string): string => {
      const last = field.split('.').pop() ?? field;
      return index[last.toLowerCase()] ?? humanizeField(last);
    },
    [index]
  );
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?)?$/;

/**
 * Un valor del modelo en palabras: `true` → "Sí", `"monthly"` → "Mensual", una fecha ISO → fecha local, un objeto
 * → JSON. Los números se muestran tal cual.
 */
export const useValueLabel = () => {
  const { t } = useTranslation();
  const values = useDictionary('enumValue');
  const index = useMemo(() => lowerIndex(values), [values]);
  return useCallback(
    (value: unknown): string => {
      if (value === null || value === undefined || value === '') return t('contractReview.value.empty');
      if (typeof value === 'boolean') return value ? t('contractReview.value.yes') : t('contractReview.value.no');
      if (typeof value === 'number') return String(value);
      if (typeof value === 'object') return JSON.stringify(value);
      const text = String(value);
      const known = index[text.toLowerCase()];
      if (known) return known;
      if (text === 'true' || text === 'false') return text === 'true' ? t('contractReview.value.yes') : t('contractReview.value.no');
      if (ISO_DATE.test(text)) {
        const date = new Date(text);
        if (!Number.isNaN(date.getTime())) return text.length > 10 && !text.endsWith('T00:00:00') ? date.toLocaleString() : date.toLocaleDateString();
      }
      return text;
    },
    [index, t]
  );
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Reemplaza en un texto libre del servicio (descripción, motivo, detalle de un chequeo) los identificadores del modelo
 * por su nombre en el idioma del usuario: "contract.paymentScheduleFrequency" → "Frecuencia de pago",
 * "SettlementPeriod" → "Período de liquidación", "ContractPaymentScheduleTypeEnum" → "forma de pago". Palabras enteras,
 * respetando mayúsculas, así no toca el texto normal.
 */
export const useHumanizeText = () => {
  const fields = useDictionary('field');
  const values = useDictionary('enumValue');
  const fieldLabel = useFieldLabel();
  const pattern = useMemo(() => {
    const names = Object.keys(fields).sort((a, b) => b.length - a.length);
    if (names.length === 0) return undefined;
    const fieldAlternatives = names.flatMap(name => [escapeRegExp(name), escapeRegExp(name[0].toLowerCase() + name.slice(1))]);
    // Sólo los valores que son claramente identificadores (dos palabras pegadas: InAnyStage, singlePayment); "Manual" o
    // "monthly" también son palabras normales y no se tocan.
    const valueAlternatives = Object.keys(values)
      .filter(name => /^[A-Za-z]+[A-Z][a-z]/.test(name) && name.length >= 6)
      .sort((a, b) => b.length - a.length)
      .map(escapeRegExp);
    // Con prefijo de entidad opcional ("contract.", "concept[3].") y nombre de enumerado ("ContractPaymentScheduleTypeEnum").
    return new RegExp(
      `\\b(?:[a-z]+(?:\\[\\d+\\])?\\.)?(${fieldAlternatives.join('|')})\\b|\\b([A-Z][A-Za-z]+Enum)\\b${valueAlternatives.length ? `|\\b(${valueAlternatives.join('|')})\\b` : ''}`,
      'g'
    );
  }, [fields, values]);
  return useCallback(
    (text: string): string => {
      if (!pattern) return text;
      return text.replace(pattern, (match, field: string | undefined, enumName: string | undefined, value: string | undefined) => {
        if (field) return `«${fieldLabel(field)}»`;
        if (enumName) return `«${fieldLabel(enumName.replace(/Enum$/, '').replace(/^Contract(?=[A-Z])/, '').replace(/^Concept(?=[A-Z])/, ''))}»`;
        if (value) return `«${values[value] ?? value}»`;
        return match;
      });
    },
    [pattern, fieldLabel, values]
  );
};
